<template>
    <StandardLayout>
        <div class="admin-moderation">
            <DoubleHeader :title="$t('admin.moderationList.title')" :backTitle="$t('admin.backBtn')" backRoute="adminMain" />

            <div v-if="busy" class="admin-loading-container">
                <Spinner />
                {{ $tc("admin.loadingData") }}
            </div>

            <div v-else-if="moderationRequests.length === 0" class="empty-state">
                <p>{{ $t("admin.moderationList.noModerationRequests") }}</p>
            </div>

            <div v-else class="card">
                <div class="table-responsive">
                    <table class="moderation-table">
                        <thead>
                            <tr>
                                <th>{{ $t("admin.moderationList.id") }}</th>
                                <th>{{ $t("admin.moderationList.postId") }}</th>
                                <th>{{ $t("admin.moderationList.type") }}</th>
                                <th>{{ $t("admin.moderationList.reportedBy") }}</th>
                                <th>{{ $t("admin.moderationList.reportedAt") }}</th>
                                <th>{{ $t("admin.moderationList.status") }}</th>
                                <th>{{ $t("admin.moderationList.acknowledgedBy") }}</th>
                                <th>{{ $t("admin.moderationList.actions") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="request in moderationRequests" :key="request.id">
                                <td :data-label="$t('admin.moderationList.id')">{{ request.id }}</td>
                                <td :data-label="$t('admin.moderationList.postId')">{{ request.postId }}</td>
                                <td :data-label="$t('admin.moderationList.type')">
                                    <span :class="['type-badge', `type-${request.postType}`]">
                                        {{ formatPostType(request.postType) }}
                                    </span>
                                </td>
                                <td :data-label="$t('admin.moderationList.reportedBy')">
                                    {{ request.reportedByName || request.reportedBy }}
                                </td>
                                <td :data-label="$t('admin.moderationList.reportedAt')">{{ request.reportedAt | prettyDateTime }}</td>
                                <td :data-label="$t('admin.moderationList.status')">
                                    <span :class="['status-badge', request.acknowledgedAt ? 'status-acknowledged' : 'status-pending']">
                                        {{
                                            request.acknowledgedAt
                                                ? $t("admin.moderationList.acknowledged")
                                                : $t("admin.moderationList.pending")
                                        }}
                                    </span>
                                </td>
                                <td :data-label="$t('admin.moderationList.acknowledgedBy')">
                                    {{ request.acknowledgedByName || "-" }}
                                </td>
                                <td :data-label="$t('admin.moderationList.actions')">
                                    <button class="review-btn" @click="reviewRequest(request)" :disabled="!!request.acknowledgedAt">
                                        {{ $t("admin.moderationList.review") }}
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="pagination-container" v-if="totalPages > 1">
                <PaginationControls :page="page" :totalPages="totalPages" @new-page="onNewPage" />
            </div>

            <ModerationReviewModal
                :show="selectedRequest !== null"
                :request="selectedRequest || {}"
                :content="requestContent"
                @close="closeReviewModal"
                @action-complete="onActionComplete"
            />
        </div>
    </StandardLayout>
</template>

<script lang="ts">
import Vue from "vue";
import StandardLayout from "../StandardLayout.vue";
import CommonComponents from "@/views/shared";
import PaginationControls from "@/views/shared/PaginationControls.vue";
import ModerationReviewModal from "./ModerationReviewModal.vue";
import { PostType, ModerationRequest } from "@/api/api";
import { setBodyScrollLock } from "@/utilities";

export default Vue.extend({
    name: "AdminModeration",
    components: {
        StandardLayout,
        ...CommonComponents,
        PaginationControls,
        ModerationReviewModal,
    },
    data() {
        return {
            moderationRequests: [] as ModerationRequest[],
            totalPages: 0,
            busy: false,
            selectedRequest: null as ModerationRequest | null,
            requestContent: "",
            pageSize: 10,
        };
    },
    computed: {
        page(): number {
            const pageQuery = this.$route.query.page;
            return pageQuery ? parseInt(pageQuery as string, 10) : 0;
        },
        isAnyModalOpen(): boolean {
            return this.selectedRequest !== null;
        },
    },
    beforeDestroy(): void {
        setBodyScrollLock(false);
    },
    watch: {
        isAnyModalOpen(val: boolean) {
            setBodyScrollLock(val);
        },
    },
    methods: {
        formatPostType(type: PostType): string {
            switch (type) {
                case PostType.DISCUSSION_POST:
                    return this.$tc("admin.moderationList.postTypes.discussionPost");
                case PostType.DATA_EVENT:
                    return this.$tc("admin.moderationList.postTypes.dataEvent");
                default:
                    return type;
            }
        },
        async reviewRequest(request: ModerationRequest) {
            this.selectedRequest = request;
            try {
                const content = await this.$services.api.getModerationContent(request.postType, request.postId);
                this.requestContent = content;
            } catch (error) {
                this.requestContent = this.$tc("admin.moderationList.errorLoadingContent");
            }
        },
        closeReviewModal() {
            this.selectedRequest = null;
            this.requestContent = "";
        },
        async onActionComplete() {
            await this.loadModerationRequests();
            this.closeReviewModal();
        },
        onNewPage(page: number) {
            window.scrollTo(0, 0);

            this.$nextTick(() => {
                this.$router
                    .push({
                        path: this.$route.path,
                        query: { page: page.toString() },
                    })
                    .then(() => {
                        this.loadModerationRequests();
                    });
            });
        },
        async loadModerationRequests() {
            this.busy = true;
            try {
                const response = await this.$services.api.getModerationRequests(this.page, this.pageSize);
                this.moderationRequests = response.requests;
                this.totalPages = response.totalPages;
            } catch (error) {
                console.error("Error loading moderation requests:", error);
            } finally {
                this.busy = false;
            }
        },
    },
    mounted() {
        if (this.$route.query.page === undefined) {
            this.$router
                .replace({
                    name: "adminModeration",
                    query: { page: "0" },
                })
                .catch((err) => {
                    if (err.name !== "NavigationDuplicated") {
                        throw err;
                    }
                });
            this.loadModerationRequests();
        } else {
            this.loadModerationRequests();
        }
    },
});
</script>

<style scoped lang="scss">
@use "src/scss/variables";
@use "src/scss/mixins";

.admin-moderation {
    display: flex;
    flex-direction: column;
    padding: 40px 40px;
    max-width: 1280px;
    text-align: left;
}

.empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    color: #6a6d71;
    font-size: 14px;
}

.card {
    background: #fff;
    border: 1px solid var(--color-border);
    border-radius: 3px;
    margin-top: 24px;
    overflow: hidden;
}

.table-responsive {
    overflow-x: auto;
}

.moderation-table {
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        padding: 12px 16px;
        text-align: left;
        font-size: 14px;
    }

    th {
        font-family: var(--font-family-bold);
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #6a6d71;
        border-bottom: 1px solid var(--color-border);
    }

    td {
        border-bottom: 1px solid #f0f0f0;
    }

    tbody tr:last-child td {
        border-bottom: none;
    }

    tbody tr {
        transition: background 0.12s ease;

        &:hover {
            background: #f9fafb;
        }
    }
}

.type-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 3px;
    font-size: 12px;
    font-family: var(--font-family-bold);
    letter-spacing: 0.3px;

    &.type-discussion_post {
        background: rgba(27, 128, 201, 0.1);
        color: var(--color-primary);
    }

    &.type-data_event {
        background: rgba(63, 133, 48, 0.1);
        color: #3f8530;
    }
}

.status-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 3px;
    font-size: 12px;
    font-family: var(--font-family-bold);
    letter-spacing: 0.3px;

    &.status-pending {
        background: rgba(27, 128, 201, 0.1);
        color: var(--color-primary);
    }

    &.status-acknowledged {
        background: rgba(63, 133, 48, 0.1);
        color: #3f8530;
    }
}

.review-btn {
    padding: 6px 16px;
    font-size: 13px;
    font-family: var(--font-family-medium);
    color: var(--color-primary);
    background: transparent;
    border: 1px solid var(--color-primary);
    border-radius: 3px;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
    white-space: nowrap;

    &:hover {
        background: var(--color-primary);
        color: #fff;
    }

    &:disabled {
        color: var(--color-border);
        border-color: var(--color-border);
        background: transparent;
        cursor: not-allowed;

        &:hover {
            background: transparent;
            color: var(--color-border);
        }
    }
}

.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: center;
}

@media (max-width: 768px) {
    .moderation-table {
        display: block;

        thead {
            display: none;
        }

        tbody,
        tr {
            display: block;
        }

        tr {
            margin-bottom: 16px;
            border: 1px solid var(--color-border);
            border-radius: 3px;
        }

        td {
            display: flex;
            justify-content: space-between;
            padding: 10px 16px;
            border-bottom: 1px solid #f0f0f0;

            &:before {
                content: attr(data-label);
                font-family: var(--font-family-bold);
                font-size: 12px;
                color: #6a6d71;
                margin-right: 10px;
            }

            &:last-child {
                border-bottom: none;
                justify-content: flex-end;

                &:before {
                    content: "";
                }
            }
        }
    }
}
</style>
